import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, Smartphone, Clock, Heart, Shield, CreditCard, Bell, Calendar, Star, Award, MessageSquare, Gamepad2, Share2, CheckCircle, Zap, Gift, AlertCircle } from 'lucide-react';
import HeaderEN from '../components/HeaderEN';
import '../styles/index.css';

const PageParentsEN = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Parents | Family Interface"; }, []);

  return (
    <div className="home-container">
      <HeaderEN currentPage="parents" lang="en" />

      {/* Hero Parents */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Users size={16} />
            <span>Parents App</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-parents)' }}>Parents</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            Order in <strong style={{ color: 'var(--actor-parents)' }}>15 seconds</strong><br />
            for all your children on one app
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">15s</div>
              <div className="stat-label">To order</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">∞</div>
              <div className="stat-label">Children managed</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Secure</div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifesto — Why EcoLunch for Parents */}
      <section className="section" data-testid="manifeste-parents">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why EcoLunch for Parents?</h2>
          </div>
          <div className="manifeste-tile" data-testid="manifeste-gratuit-parents">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#3B82F6'}}><Gift size={30} /></div>
              <h3>Free for schools. Free for parents. Sustainable for the ecosystem.</h3>
            </div>
            <p>EcoLunch will be and remain free for schools. Better yet, our model also allows donation contributions, so a school can not only offer a structured, modern and frictionless service, but also derive a positive benefit from it.</p>
            <p>For parents, platform usage is also free. The goal is clear: make access to the service <strong>simple, smooth, centralized and without unnecessary barriers</strong>.</p>
          </div>
          <div className="manifeste-tile" data-testid="manifeste-coparentalite">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#A855F7'}}><Heart size={30} /></div>
              <h3>Designed for real family life, including co-parenting</h3>
            </div>
            <p>EcoLunch also integrates a co-parenting portal, because the reality of families is not uniform. The platform was designed to reflect the real daily life of parents, including when ordering, payment, tracking and organization responsibilities need to be managed between multiple adults.</p>
            <p>This approach is part of our difference: we are not developing a theoretical platform. We are developing <strong>a platform rooted in the reality of life here</strong>.</p>
          </div>
        </div>
      </section>

      {/* Features Section - ALL features */}
      <section id="features" className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Parent Features</h2>
            <p className="section-subtitle">
              All features to easily manage your children's meals
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Simplified login</h3>
              <p>Quick login with Google, Facebook, Apple ID, Microsoft without needing to manually create an account.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><AlertCircle size={32} /></div>
              <h3>Smart allergy management</h3>
              <p>Complete allergen management in an intelligent way to ensure your children's food safety.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Star size={32} /></div>
              <h3>Preference memory</h3>
              <p>Make your child happy for sure! The platform allows you to rate and keep in memory your child's appreciation of dishes.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Email notifications</h3>
              <p>Receive updates and deadlines and reminders by email to never miss an order period.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CreditCard size={32} /></div>
              <h3>Secure payment</h3>
              <p>The only platform offering you the widest range of payment methods on the market: Google Pay, Apple Pay, Credit Card, Debit Credit, Interac Direct Transfer.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CreditCard size={32} /></div>
              <h3>Sezzle - Split payment</h3>
              <p>Thanks to its exclusive partnership with Sezzle Canada, EcoLunch makes split payments accessible to all parents. Pay in 4 installments spread over 6 weeks.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Smartphone size={32} /></div>
              <h3>Export & Print menus</h3>
              <p>Print or download ordered meals directly. This poster can be used as a visual reminder for you and your children.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Award size={32} /></div>
              <h3>Custom themes</h3>
              <p>Change your platform's display theme. Choose from: Light, Dark, Neon, Neutral.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Calendar size={32} /></div>
              <h3>Calendar synchronization</h3>
              <p>Integrate ordered meals into your digital calendar, Google, Apple, Microsoft and others for optimal family organization.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Color code per child</h3>
              <p>Facilitate navigation by assigning a different color for each child when navigating the platform.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Quick order menu</h3>
              <p>Customize the caterer's complete menu according to rotation to adapt the experience to your preferences.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>C1C (1-Click Order)</h3>
              <p>Order for the week or month in ONE click thanks to the personalized quick order option.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Clock size={32} /></div>
              <h3>Automatic meal subscription</h3>
              <p>Once your menus are customized you can create a recurring subscription that will be effective throughout the school year.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Smartphone size={32} /></div>
              <h3>SMS & Push notifications</h3>
              <p>Receive alerts by text message and push notifications directly on your mobile phone to never miss important information.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Heart size={32} /></div>
              <h3>Detailed history</h3>
              <p>Consult complete history of orders and payments with filtering options by period, child and meal type.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Co-parenting Section */}
      <section className="section coparent-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Simplified co-parenting</h2>
            <p className="section-subtitle">
              For separated families: share management without complexity
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Share2 size={32} /></div>
              <h3>Automatic shared custody</h3>
              <p>Configure custody days. The responsible parent receives notifications at the right time.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Separate accounts, shared children</h3>
              <p>Each parent has their independent account. Both see order history.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CreditCard size={32} /></div>
              <h3>Smart billing</h3>
              <p>Separate invoices according to custody schedule. Total transparency for both parents.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Simplified communication</h3>
              <p>Internal messaging to coordinate without friction. Shared decision history.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Joint approval</h3>
              <p>Approval mode for important decisions: allergy changes, new menus, etc.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Calendar size={32} /></div>
              <h3>Shared calendar</h3>
              <p>View custody schedule and upcoming orders. Automatic synchronization between both accounts.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Child Portal Section */}
      <section className="section game-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Child Portal - Dedicated Space</h2>
            <p className="section-subtitle">
              A secure and fun universe to involve your children
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Child Portal</h3>
              <p>Secure dedicated space where children can browse, play, customize, create their Avatars, order their meals and track their rewards.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Gamepad2 size={32} /></div>
              <h3>CliC & CroQ™ Order Game</h3>
              <p>Gamified ordering interface for children with points and rewards system. Children choose their meals in a fun and interactive way.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Avatar creation</h3>
              <p>Children create their own personalized avatar that appears in their interface and on their printed menus.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Award size={32} /></div>
              <h3>Theme selection</h3>
              <p>Let your children personalize their experience with fun themes: Sports, Nature, Motocross, Unicorns, Space, Animals and more.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Star size={32} /></div>
              <h3>Child rating system</h3>
              <p>Parents and children rate meals. Statistics are separated for caterers, schools and management to better understand everyone's preferences.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>Challenges and missions</h3>
              <p>Weekly food challenges to encourage discovery of new foods. Rewards and badges to collect.</p>
            </div>
          </div>
        </div>
      </section>

      {/* EcoPulse CC Mini-Card */}
      <section className="section" data-testid="ecopulse-mini-parents">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-mini-card" style={{borderColor: 'rgba(139, 92, 246, 0.4)'}}>
            <div className="ecopulse-mini-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Zap size={28} />
              </div>
              <div>
                <h3>EcoPulse CC™ <span style={{fontSize: '0.85rem', fontWeight: 500, opacity: 0.7}}>for Parents</span></h3>
                <p style={{fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0}}>
                  One sentence is all you need
                </p>
              </div>
            </div>
            <div className="ecopulse-mini-commands">
              <div className="ecopulse-mini-cmd">Change Emma's meal to salad Thursday</div>
              <div className="ecopulse-mini-cmd">Cancel Friday for both children</div>
              <div className="ecopulse-mini-cmd">Show me my balance</div>
              <div className="ecopulse-mini-cmd">Put Lucas on 4 days/week starting Monday</div>
            </div>
            <button onClick={() => navigate('/en/ecopulse')} className="ecopulse-mini-link">
              Discover all EcoPulse commands <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                The unified platform to modernize school meals in Quebec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/en/caterers')}>For Caterers</a>
                <a onClick={() => navigate('/en/parents')}>For Parents</a>
                <a onClick={() => navigate('/en/schools')}>For Schools</a>
                <a onClick={() => navigate('/en/directions')}>For Boards</a>
                <a onClick={() => navigate('/en/css')}>For CSS</a>
              </div>

              <div className="footer-column">
                <h4>Platform</h4>
                <a onClick={() => navigate('/en/platform')}>Architecture</a>
                <a onClick={() => navigate('/en/coparenting')}>Co-Parenting</a>
                <a onClick={() => navigate('/en/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/en/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageParentsEN;
